import React from 'react';
import Localize from 'react-intl-universal';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { formatPrice } from '@common/Util.jsx';
import { VAT_BOOKING_PROCESS } from '@pages/BookingProcess/Constants';

import OrderSummaryDialog from './OrderSummaryDialog';
import OrderSummaryWithParticipants from './OrderSummaryWithParticipants';

const StyledBox = styled(Box)(({ theme }) => ({
  fontFamily: `${theme.typography.special.fontFamily} !important`,
  width: '100%',
  padding: '2rem 1rem 1rem 1rem',
  overflow: 'auto',
  backgroundColor: [theme.palette?.booking.list],
  '& .MuiTypography-root': {
    fontFamily: `${theme.typography.special.fontFamily} !important`
  },
  [theme.breakpoints.up('md')]: {
    height: '30vh'
  },
  [theme.breakpoints.up('lg')]: {
    height: '65vh'
  }
}));

const StyledInfo = styled(Box)(({ theme }) => ({
  backgroundColor: [theme.palette.booking.info],
  padding: '0.75rem',
  paddingBottom: '0,4rem',
  '& .MuiTypography-root': {
    color: `${theme.palette.booking.header} !important`,
    lineHeight: '0.8 ',
    fontFamily: `${theme.typography.special.fontFamily} !important`
  }
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: `${theme.palette.booking.header} !important`,
  fontSize: '1.25rem !important',
  marginBottom: '2rem !important',
  fontFamily: `${theme.typography.special.fontFamily} !important`
}));

const StyledGrid = styled(Grid)(() => ({
  '& .MuiTypography-root': {
    fontWeight: 600,
    fontSize: '1.188rem'
  }
}));

const StyledPriceContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 8
}));

const OrderSummary = ({
  selectedSessions,
  totalObj,
  activeStep,
  participants,
  participantPriceAndDiscounts,
  steps,
  isCheckoutFormValid,
  eventData,
  contextData,
  contactsInfo,
  availableSeats,
  goToDetails,
  checkedConsents,
  isEventFree = false
}) => {
  const subtotal = activeStep > 1 ? participantPriceAndDiscounts?.subtotal : totalObj?.subtotal;
  const vat = activeStep > 1 ? participantPriceAndDiscounts?.vat : totalObj?.vat;
  const total = activeStep > 1 ? participantPriceAndDiscounts?.total : totalObj?.total;

  return (
    <StyledBox>
      <OrderSummaryDialog
        eventData={eventData}
        steps={steps}
        isCheckoutFormValid={isCheckoutFormValid}
        activeStep={activeStep}
        contextData={contextData}
        participants={participants}
        contactsInfo={contactsInfo}
        selectedSessions={selectedSessions}
        availableSeats={availableSeats}
        goToDetails={goToDetails}
        checkedConsents={checkedConsents}
        isEventFree={isEventFree}
      />

      <StyledTypography>{Localize.get('Booking.orderSummary')}</StyledTypography>
      {activeStep > 1 ? (
        <OrderSummaryWithParticipants
          participants={participants}
          selectedSessions={selectedSessions}
          participantPriceAndDiscounts={participantPriceAndDiscounts}
          isEventFree={isEventFree}
        />
      ) : (
        <StyledInfo>
          <Grid container item>
            <Grid item xs={1}>
              <Typography display='inline'>{'1x '}</Typography>
            </Grid>
            <Grid item xs={7} sx={{ overflowWrap: 'anywhere' }}>
              <Typography display='inline'>{totalObj?.eventName}</Typography>
            </Grid>
            <Grid item xs={4} sx={{ flexShrink: 0, textAlign: 'end', alignContent: 'end' }}>
              {isEventFree ? (
                Localize.get('CustomerLoyaltyEvents.freeOfCharge')
              ) : (
                <StyledPriceContainer>
                  <Typography>{formatPrice(totalObj?.eventPrice)}</Typography>
                  {!!totalObj?.eventPrice && <Typography>{totalObj?.currency}</Typography>}
                </StyledPriceContainer>
              )}
            </Grid>
          </Grid>
          {selectedSessions
            .filter((session) => !session?.optionalSession)
            .map((selectedSession, index) => (
              <Grid key={index} container item>
                <Grid item xs={1} />
                <Grid item xs={1}>
                  <Typography sx={{ fontSize: '0.9rem' }} display='inline'>
                    {'1x '}
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography
                    sx={{ fontSize: '0.9rem' }}
                    display='inline'
                  >{`${selectedSession?.name} (${Localize.get('Booking.mandatory')})`}</Typography>
                </Grid>
                <Grid item xs={1} />
              </Grid>
            ))}
          {selectedSessions
            .filter((session) => session?.optionalSession)
            .map((selectedSession, index) => (
              <Grid key={index} container item>
                <Grid item xs={1}>
                  <Typography display='inline'>{'1x '}</Typography>
                </Grid>
                <Grid item xs={7} sx={{ overflowWrap: 'anywhere' }}>
                  <Typography display='inline'>{`${selectedSession?.name} (${Localize.get('Booking.optional')})`}</Typography>
                </Grid>
                <Grid item xs={4} sx={{ flexShrink: 0, textAlign: 'end', alignContent: 'end' }}>
                  {isEventFree ? (
                    Localize.get('CustomerLoyaltyEvents.freeOfCharge')
                  ) : (
                    <StyledPriceContainer>
                      <Typography>{formatPrice(selectedSession?.price)}</Typography>
                      {!!selectedSession?.price && <Typography>{totalObj?.currency}</Typography>}
                    </StyledPriceContainer>
                  )}
                </Grid>
              </Grid>
            ))}
        </StyledInfo>
      )}
      {!!participantPriceAndDiscounts?.vat && (
        <>
          <Divider sx={{ mt: '2rem', mb: '0.5rem' }} />
          <Grid container>
            <Grid item xs={9}>
              <Typography display='inline'>{Localize.get('Booking.subtotal')}</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'end' }}>
              {isEventFree ? (
                Localize.get('CustomerLoyaltyEvents.freeOfCharge')
              ) : (
                <StyledPriceContainer>
                  <Typography>{formatPrice(subtotal)}</Typography>
                  {!!subtotal && <Typography>{totalObj?.currency}</Typography>}
                </StyledPriceContainer>
              )}
            </Grid>

            <Grid item xs={9}>
              <Typography display='inline'>
                {`${Localize.get('Booking.vat')} ${VAT_BOOKING_PROCESS * 100}%`}
              </Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'end' }}>
              <StyledPriceContainer>
                <Typography>{(vat || 0).toFixed(2)}</Typography>
                {!!vat && <Typography>{totalObj?.currency}</Typography>}
              </StyledPriceContainer>
            </Grid>
          </Grid>
        </>
      )}
      <Divider sx={{ mt: '0.5rem', mb: '0.5rem' }} />
      <Grid container>
        <StyledGrid item xs={8}>
          <Typography display='inline'>{`${Localize.get('Booking.total')}*`}</Typography>
        </StyledGrid>
        <StyledGrid item xs={4} sx={{ textAlign: 'end' }}>
          <StyledPriceContainer>
            <Typography>
              {isEventFree
                ? Localize.get('CustomerLoyaltyEvents.freeOfCharge')
                : formatPrice(total)}
            </Typography>

            {!!total && <Typography>{totalObj?.currency}</Typography>}
          </StyledPriceContainer>
        </StyledGrid>
      </Grid>
      {eventData?.hasPricesAndDiscountsFeature && (
        <Grid container>
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 400, fontSize: '1rem', p: 2 }}>
              {Localize.get('Booking.vatNotIncluded')}
            </Typography>
          </Grid>
        </Grid>
      )}
    </StyledBox>
  );
};
export default OrderSummary;
